import { faStar } from "@jewlr/pro-regular-svg-icons/faStar"
import { faStar as solidStar } from "@jewlr/pro-solid-svg-icons/faStar"
import { faStarHalfAlt as solidStarHalf } from "@jewlr/pro-solid-svg-icons/faStarHalfAlt"
import PropTypes from "prop-types"
import { useState } from "react"
import { Link } from "react-router-dom"

import LooksSelector from "areas/collection/looks-selector"
import ProductDescription from "areas/collection/products/product-description"
import { getPlaceholderData } from "areas/collection/products/product-image"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "components/ui/carousel"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Image from "components/ui/image"
import { jewlr_t } from "helpers/application"
import { to_currency } from "helpers/number"
import { roundToHalf } from "helpers/round-to-half"

const ProductCard = ({ index, linkClickData, product }) => {
  const [selectedLook, setSelectedLook] = useState(setDefaultLook(product))
  const selectedProductLook = product.looks[selectedLook]

  if (!selectedProductLook) {
    // Prevents selectedLook from being undefined and breaking the component on reload of products.
    // Gives useEffect a chance to update.
    return null
  }

  const placeholder = getPlaceholderData(product.product_type)

  const showRatings =
    product.reviews &&
    product.reviews.count > 0 &&
    product.reviews.average_rating >= 4

  const onSale = selectedProductLook.reg !== selectedProductLook.selling

  return (
    <CarouselItem className="basis-150">
      <Link
        className="block focus-visible:-outline-offset-2"
        data-lc={linkClickData}
        to={`/products/${product.style_code}/${product.permalink}`}
      >
        <Image
          alt={product.name}
          className="[&.lazyloading]:shimmer w-full"
          fallback={require(
            `images/collections/placeholder_masks/${
              placeholder[0]
            }_${(index % placeholder[1]) + 1}.svg`
          )}
          height="600"
          sizes="150px"
          srcSet={selectedProductLook.images?.[0]}
          width="600"
        />
      </Link>
      <div
        className="text-12 desktop:my-16 mb-12 mt-8 flex h-12 items-center gap-6"
        {...(showRatings && {
          "aria-label": `Rated ${product.reviews.average_rating.toFixed(1)} out of 5 from ${product.reviews.count} reviews`,
        })}
      >
        {showRatings && (
          <>
            <span className="text-rating flex gap-2">
              {(() => {
                const average_rating = roundToHalf(
                  product.reviews.average_rating,
                  0.5
                )
                const starArray = []

                for (let i = 1; i <= 5; i++) {
                  if (i <= average_rating) {
                    starArray.push(<FontAwesomeIcon icon={solidStar} key={i} />)
                  } else if (i <= average_rating + 0.5) {
                    starArray.push(
                      <FontAwesomeIcon icon={solidStarHalf} key={i} />
                    )
                  } else {
                    starArray.push(<FontAwesomeIcon icon={faStar} key={i} />)
                  }
                }

                return starArray
              })()}
            </span>
            <span className="text-12 leading-12 font-semibold">
              {product.reviews.count}&nbsp;Reviews
            </span>
          </>
        )}
      </div>
      <Link
        className="block"
        data-lc={linkClickData}
        tabIndex="-1"
        to={`/products/${product.style_code}/${product.permalink}`}
      >
        <ProductDescription productName={product.name} size="sm" />
      </Link>
      {selectedProductLook.selling && (
        <p className="text-12 desktop:mb-6 mb-4">
          from{" "}
          {onSale ? (
            <>
              <span className="text-emerald font-semibold">
                {to_currency(selectedProductLook.selling, {
                  precision: 0,
                })}
              </span>
              <strike className="text-grey-300 mx-3 font-semibold">
                {to_currency(selectedProductLook.reg, { precision: 0 })}
              </strike>
              <span className="text-emerald font-semibold">
                SAVE{" "}
                {(
                  ((selectedProductLook.reg - selectedProductLook.selling) /
                    selectedProductLook.reg) *
                  100
                ).toFixed(0)}
                %
              </span>
            </>
          ) : (
            <span className="font-semibold">
              {to_currency(selectedProductLook.reg, { precision: 0 })}
            </span>
          )}
        </p>
      )}
      <LooksSelector
        className="desktop:pt-12 justify-start pb-0 pt-8"
        looks={product.looks}
        product={product}
        selectedLook={selectedLook}
        updateLook={(look) => setSelectedLook(look)}
      />
      {product.is_jlux && (
        <p className="text-neutral text-12 font-light italic">
          Plus {jewlr_t("terms.center")} stone
        </p>
      )}
    </CarouselItem>
  )
}

ProductCard.propTypes = {
  index: PropTypes.number,
  linkClickData: PropTypes.string,
  product: PropTypes.object,
}

const ProductsList = ({ linkClickData, name, products }) => {
  return (
    <Carousel
      description={name}
      opts={{
        dragFree: true,
        loop: false,
        slidesToScroll: "auto",
      }}
    >
      <CarouselContent className="gap-24">
        {products.map((product, index) => (
          <ProductCard
            index={index}
            key={`prec${product.name}${index}`}
            linkClickData={linkClickData}
            product={product}
          />
        ))}
      </CarouselContent>
      <CarouselPrevious className="inline-flex" />
      <CarouselNext className="inline-flex" />
    </Carousel>
  )
}

const setDefaultLook = (product) => {
  return Object.prototype.hasOwnProperty.call(
    product.looks,
    product.default_look
  )
    ? product.default_look
    : Object.keys(product.looks)[0]
}

ProductsList.propTypes = {
  linkClickData: PropTypes.string,
  name: PropTypes.string,
  products: PropTypes.array,
}

export default ProductsList
